
import * as Three from 'three';
import { selected_object, setPushIntersectedObject } from '../../redux/Slice/gridSlice';
import { addParticle } from '../../redux/Slice/particleSlice';
import { getDefaultParticleShape } from './particleShapeTextures';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import toast from 'react-hot-toast';
import store from '../../redux/store';
import handleTerrain from './handleTerrain';
import handleRain from './handleRain';
import handleSnow from './handleSnow';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  handleClouds,
  handleFire,
  handleFoliage,
  handleStars,
  handleWater,
} from '../Elements';
import {
  FOLIAGE_BUSH_COLOR_DEFAULT,
  FOLIAGE_GRASS_COLOR_DEFAULT,
  FOLIAGE_TREE_COLOR_DEFAULTS,
  createFoliageAnimateFunction,
  repairFoliageCloneReferences,
  resolveFoliageAnimationConfig,
} from './handleFoliage';
import { startAnimation } from './animationControls';

function pickParticleField(item, userData, key, fallback) {
  if (userData?.[key] !== undefined && userData?.[key] !== null) {
    return userData[key];
  }
  if (item?.[key] !== undefined && item?.[key] !== null) {
    return item[key];
  }
  return fallback;
}

function buildFoliageDuplicateProps(item, originalParticleObj) {
  const userData = originalParticleObj?.userData || {};

  const grassColor = pickParticleField(
    item,
    userData,
    'grassColor',
    pickParticleField(item, userData, 'color', FOLIAGE_GRASS_COLOR_DEFAULT)
  );
  const bushColor = pickParticleField(
    item,
    userData,
    'bushColor',
    pickParticleField(item, userData, 'color', FOLIAGE_BUSH_COLOR_DEFAULT)
  );

  let opacity = pickParticleField(item, userData, 'opacity', undefined);
  if (opacity === undefined && userData.material_settings?.opacity !== undefined) {
    opacity = userData.material_settings.opacity;
  }
  if (opacity === undefined) {
    opacity = 1.0;
  }

  const props = {
    treeCount: pickParticleField(item, userData, 'treeCount', 10),
    bushCount: pickParticleField(item, userData, 'bushCount', 10),
    grassCount: pickParticleField(item, userData, 'grassCount', 50),
    forestRadius: pickParticleField(item, userData, 'forestRadius', 3),
    forestSpread: pickParticleField(item, userData, 'forestSpread', 40),
    range: pickParticleField(item, userData, 'range', 10),
    pineColor: pickParticleField(
      item,
      userData,
      'pineColor',
      FOLIAGE_TREE_COLOR_DEFAULTS.pine
    ),
    oakColor: pickParticleField(
      item,
      userData,
      'oakColor',
      FOLIAGE_TREE_COLOR_DEFAULTS.oak
    ),
    birchColor: pickParticleField(
      item,
      userData,
      'birchColor',
      FOLIAGE_TREE_COLOR_DEFAULTS.birch
    ),
    grassColor,
    bushColor,
    color: grassColor,
    opacity,
    size: pickParticleField(item, userData, 'size', 1.0),
    ...resolveFoliageAnimationConfig({ ...item, ...userData }),
    life: pickParticleField(item, userData, 'life', 0),
    isPlaying: pickParticleField(item, userData, 'isPlaying', true),
    isStopped: pickParticleField(item, userData, 'isStopped', false),
  };

  const swayIntensity = pickParticleField(item, userData, 'swayIntensity', undefined);
  if (swayIntensity !== undefined) {
    props.swayIntensity = swayIntensity;
  }

  const leafCount = pickParticleField(item, userData, 'leafCount', undefined);
  if (leafCount !== undefined) {
    props.leafCount = leafCount;
  }

  return props;
}

function buildFoliageDuplicateItem(item, originalParticleObj, newId, particleType) {
  const foliageProps = buildFoliageDuplicateProps(item, originalParticleObj);

  return {
    id: newId,
    name: item.name || originalParticleObj?.name,
    type: particleType,
    position: {
      x: originalParticleObj?.position.x ?? item.position?.x ?? 0,
      y: originalParticleObj?.position.y ?? item.position?.y ?? 0,
      z: originalParticleObj?.position.z ?? item.position?.z ?? 0,
    },
    rotation: {
      x: originalParticleObj?.rotation.x ?? item.rotation?.x ?? 0,
      y: originalParticleObj?.rotation.y ?? item.rotation?.y ?? 0,
      z: originalParticleObj?.rotation.z ?? item.rotation?.z ?? 0,
    },
    scale: {
      x: originalParticleObj?.scale.x ?? item.scale?.x ?? 1,
      y: originalParticleObj?.scale.y ?? item.scale?.y ?? 1,
      z: originalParticleObj?.scale.z ?? item.scale?.z ?? 1,
    },
    saved: false,
    isDuplicate: true,
    ...foliageProps,
  };
}

function cloneFoliageMaterials(node) {
  if (node.material) {
    if (Array.isArray(node.material)) {
      node.material = node.material.map((mat) => {
        const clonedMat = mat.clone();
        if (clonedMat.color?.clone) {
          clonedMat.color = clonedMat.color.clone();
        }
        return clonedMat;
      });
    } else {
      node.material = node.material.clone();
      if (node.material.color?.clone) {
        node.material.color = node.material.color.clone();
      }
    }
  }
}

// Map particle types to their handlers
function getParticleHandler(type) {
  switch (String(type || '').toLowerCase()) {
    case 'fire':
      return handleFire;
    case 'rain':
      return handleRain;
    case 'snow':
      return handleSnow;
    case 'water':
      return handleWater;
    case 'clouds':
    case 'cloud':
      return handleClouds;
    case 'foliage':
      return handleFoliage;
    case 'stars':
    case 'star':
      return handleStars;
    default:
      return null;
  }
}

/**
 * UI lists often set item.type = 'particle' (asset category), wiping rain/fire/etc.
 * Prefer the live scene objType, then any preserved effect type field.
 */
function resolveParticleEffectType(item, sceneObj) {
  const candidates = [
    sceneObj?.objType,
    item?.objType,
    item?.particleEffectType,
    item?.userData?.objType,
    item?.userData?.type,
    item?.type,
  ];

  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const key = String(raw).toLowerCase();
    if (key === 'particle' || key === '3d') continue;
    if (key === 'terrain' || getParticleHandler(key)) return key;
  }
  return null;
}

function buildParticleDuplicateId(effectType, scene) {
  const base = effectType && effectType !== 'particle' ? effectType : 'particle';
  let newId = `${base}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  // Extremely unlikely collision, but keep names unique in the scene tree.
  if (scene?.getObjectByName?.(newId)) {
    newId = `${base}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
  return newId;
}

function pickDuplicateNumeric(item, userData, key, fallback) {
  if (userData?.[key] !== undefined && userData?.[key] !== null) {
    return userData[key];
  }
  if (item?.[key] !== undefined && item?.[key] !== null) {
    return item[key];
  }
  return fallback;
}

export function duplicateParticleAsset(item, objectt) {
  if (!item) {
    toast.error('Invalid particle item');
    return;
  }

  if (!objectt) {
    console.error('objectt is undefined');
    toast.error('Unable to duplicate particle: missing context');
    return;
  }

  const { scene, transformControls, setPolyCount } = objectt;

  if (!store) {
    console.error('store is undefined');
    toast.error('Unable to duplicate particle: missing store');
    return;
  }

  if (!scene) {
    console.error('scene is undefined in objectt');
    toast.error('Unable to duplicate particle: missing scene');
    return;
  }

  // Clear selection
  if (transformControls) transformControls.detach();
  store.dispatch(selected_object(null));
  store.dispatch(setLoadingMessage(true));

  try {
    // Get the original particle object from the scene
    const originalParticleObj =
      scene.getObjectByName(item.id) ||
      scene.getObjectByName(item._id) ||
      scene.getObjectByName(item.name) ||
      scene.getObjectByName(item.url) ||
      null;

    const particleType = resolveParticleEffectType(item, originalParticleObj);
    const newId = buildParticleDuplicateId(particleType || 'particle', scene);
    const originalUserData = originalParticleObj?.userData || {};

    // Check if this is a terrain particle
    const isTerrain =
      particleType === 'terrain' ||
      item.type === 'terrain' ||
      item.objType === 'terrain' ||
      (originalParticleObj && originalParticleObj.objType === 'terrain');

    if (isTerrain) {
      const terrainUserData = originalUserData;

      const duplicateItem = {
        id: newId,
        type: 'terrain',
        parent: null,

        position: {
          x: (originalParticleObj?.position.x ?? item.position?.x ?? 0) + 2,
          y: originalParticleObj?.position.y || item.position?.y || 0,
          z: (originalParticleObj?.position.z ?? item.position?.z ?? 0) + 2,
        },
        rotation: {
          x:
            originalParticleObj?.rotation.x !== undefined
              ? originalParticleObj.rotation.x
              : item.rotation?.x || 0,
          y:
            originalParticleObj?.rotation.y !== undefined
              ? originalParticleObj.rotation.y
              : item.rotation?.y || 0,
          z:
            originalParticleObj?.rotation.z !== undefined
              ? originalParticleObj.rotation.z
              : item.rotation?.z || 0,
        },
        scale: {
          x: originalParticleObj?.scale.x || item.scale?.x || 1,
          y: originalParticleObj?.scale.y || item.scale?.y || 1,
          z: originalParticleObj?.scale.z || item.scale?.z || 1,
        },
        terrainWidth: terrainUserData.terrainWidth || item.terrainWidth || 256,
        terrainHeight:
          terrainUserData.terrainHeight || item.terrainHeight || 256,
        terrainSize: terrainUserData.terrainSize || item.terrainSize || 25,
        color: terrainUserData.color || item.color || '#ffffff',
        intensity: terrainUserData.intensity || item.intensity || 1,
        opacity:
          terrainUserData.opacity !== undefined
            ? terrainUserData.opacity
            : item.opacity !== undefined
              ? item.opacity
              : 1,
        isPlaying: item.isPlaying ?? true,
        isStopped: item.isStopped ?? false,
        saved: false,
        isDuplicate: true,
      };

      store.dispatch(addParticle(duplicateItem));
      handleTerrain(scene, duplicateItem);

      const newTerrain = scene.getObjectByName(newId);
      if (newTerrain) {
        newTerrain.position.set(
          duplicateItem.position.x,
          duplicateItem.position.y,
          duplicateItem.position.z
        );
        newTerrain.rotation.set(
          duplicateItem.rotation.x || 0,
          duplicateItem.rotation.y || 0,
          duplicateItem.rotation.z || 0
        );
        newTerrain.scale.set(
          duplicateItem.scale.x,
          duplicateItem.scale.y,
          duplicateItem.scale.z
        );

        setTimeout(() => {
          const terrainObj = scene.getObjectByName(newId);
          if (terrainObj) {
            store.dispatch(selected_object(terrainObj));
            if (transformControls) transformControls.attach(terrainObj);
          } else {
            store.dispatch(selected_object(newTerrain));
            if (transformControls) transformControls.attach(newTerrain);
          }
        }, 50);

        store.dispatch(setLoadingMessage(false));
        toast.success('Terrain duplicated successfully');
        return;
      }
    }

    const particleHandler = getParticleHandler(particleType);

    if (particleHandler) {
      if (particleType === 'foliage' && originalParticleObj) {
        const duplicateItem = buildFoliageDuplicateItem(
          item,
          originalParticleObj,
          newId,
          particleType
        );
        duplicateItem.parent = null;
        duplicateItem.position = {
          x: (duplicateItem.position?.x ?? 0) + 2,
          y: duplicateItem.position?.y ?? 0,
          z: (duplicateItem.position?.z ?? 0) + 2,
        };

        const foliageClone = originalParticleObj.clone(true);
        foliageClone.traverse((child) => cloneFoliageMaterials(child));
        repairFoliageCloneReferences(foliageClone);

        foliageClone.name = newId;
        foliageClone.url = newId;
        foliageClone.deleteType = 'particle';
        foliageClone.objType = 'foliage';
        foliageClone.glb_type = originalParticleObj.glb_type || 'shapes';
        foliageClone.createdAt = new Date().toISOString();
        foliageClone.position.set(
          duplicateItem.position.x,
          duplicateItem.position.y,
          duplicateItem.position.z
        );
        foliageClone.rotation.copy(originalParticleObj.rotation);
        foliageClone.scale.copy(originalParticleObj.scale);

        const clonedUserData = JSON.parse(
          JSON.stringify(originalParticleObj.userData || {})
        );
        const animationConfig = resolveFoliageAnimationConfig({
          ...clonedUserData,
          ...duplicateItem,
        });

        foliageClone.userData = {
          ...clonedUserData,
          ...duplicateItem,
          ...animationConfig,
          id: newId,
          isDuplicate: true,
          originalId: item._id || item.id,
          saved: false,
          animationId: null,
          animateFunction: null,
          createTree: originalParticleObj.userData?.createTree,
          createBush: originalParticleObj.userData?.createBush,
          createGrass: originalParticleObj.userData?.createGrass,
        };

        scene.add(foliageClone);
        store.dispatch(setPushIntersectedObject(foliageClone));
        store.dispatch(addParticle(duplicateItem));

        const animate = createFoliageAnimateFunction(foliageClone);
        foliageClone.userData.animateFunction = animate;
        if (!duplicateItem.isStopped) {
          startAnimation(foliageClone, animate, foliageClone.name);
        }

        if (setPolyCount) setPolyCount((v) => v + 1);

        setTimeout(() => {
          const newParticleObj = scene.getObjectByName(newId);
          if (newParticleObj) {
            store.dispatch(selected_object(newParticleObj));
            selectObjectInScene(newParticleObj.name);
            if (transformControls) transformControls.attach(newParticleObj);
          }
        }, 50);

        store.dispatch(setLoadingMessage(false));
        toast.success('Particle duplicated successfully');
        return;
      }

      // Build duplicate item with all original properties (scene userData fills gaps)
      const duplicateItem = {
        id: newId,
        type: particleType,
        parent: null,
        name: item.name || originalUserData.name || particleType,

        range: pickDuplicateNumeric(item, originalUserData, 'range', 1),
        height: pickDuplicateNumeric(item, originalUserData, 'height', undefined),
        intensity: pickDuplicateNumeric(item, originalUserData, 'intensity', 50),
        color: item.color || originalUserData.color || '#FFFFFF',
        position: {
          x: (originalParticleObj?.position.x ?? item.position?.x ?? 0) + 2,
          y: originalParticleObj?.position.y ?? item.position?.y ?? 0,
          z: (originalParticleObj?.position.z ?? item.position?.z ?? 0) + 2,
        },
        rotation: {
          x:
            originalParticleObj?.rotation.x !== undefined
              ? originalParticleObj.rotation.x
              : item.rotation?.x || 0,
          y:
            originalParticleObj?.rotation.y !== undefined
              ? originalParticleObj.rotation.y
              : item.rotation?.y || 0,
          z:
            originalParticleObj?.rotation.z !== undefined
              ? originalParticleObj.rotation.z
              : item.rotation?.z || 0,
        },
        scale: {
          x: originalParticleObj?.scale.x || item.scale?.x || 1,
          y: originalParticleObj?.scale.y || item.scale?.y || 1,
          z: originalParticleObj?.scale.z || item.scale?.z || 1,
        },
        isPlaying: item.isPlaying ?? originalUserData.isPlaying ?? true,
        isStopped: item.isStopped ?? originalUserData.isStopped ?? false,
        particleCount: pickDuplicateNumeric(
          item,
          originalUserData,
          'particleCount',
          1000
        ),
        size: pickDuplicateNumeric(item, originalUserData, 'size', 1.0),
        opacity: pickDuplicateNumeric(item, originalUserData, 'opacity', 0.9),
        sizeAttenuation: item.sizeAttenuation ?? true,
        blending: item.blending || 'AdditiveBlending',
        shape:
          item.shape ||
          originalUserData.shape ||
          getDefaultParticleShape(particleType),
        shapeRotation: pickDuplicateNumeric(
          item,
          originalUserData,
          'shapeRotation',
          0
        ),
        shapeScale: pickDuplicateNumeric(
          item,
          originalUserData,
          'shapeScale',
          1.0
        ),
        velocity: item.velocity || { x: 0, y: 0, z: 0 },
        acceleration: item.acceleration || { x: 0, y: 0, z: 0 },
        turbulence: pickDuplicateNumeric(
          item,
          originalUserData,
          'turbulence',
          0.1
        ),
        wind: item.wind || { x: 0, y: 0, z: 0 },
        windStrength: pickDuplicateNumeric(
          item,
          originalUserData,
          'windStrength',
          undefined
        ),
        life: pickDuplicateNumeric(item, originalUserData, 'life', 120),
        lifeVariation: pickDuplicateNumeric(
          item,
          originalUserData,
          'lifeVariation',
          0.2
        ),
        colorVariation: item.colorVariation || 0.1,
        colorTransition: item.colorTransition ?? true,
        sizeVariation: item.sizeVariation || 0.3,
        sizeTransition: item.sizeTransition ?? true,
        ...(item.coreCount && { coreCount: item.coreCount }),
        ...(item.emberCount && { emberCount: item.emberCount }),
        ...(item.heatIntensity && { heatIntensity: item.heatIntensity }),
        ...(item.dropSize != null || originalUserData.dropSize != null
          ? {
              dropSize: pickDuplicateNumeric(
                item,
                originalUserData,
                'dropSize',
                0.1
              ),
            }
          : {}),
        ...(item.splashEffect && { splashEffect: item.splashEffect }),
        ...(item.flakeSize && { flakeSize: item.flakeSize }),
        ...(item.windEffect && { windEffect: item.windEffect }),
        ...(item.twinkleSpeed && { twinkleSpeed: item.twinkleSpeed }),
        ...(item.twinkleIntensity && {
          twinkleIntensity: item.twinkleIntensity,
        }),
        ...(item.terrainWidth && { terrainWidth: item.terrainWidth }),
        ...(item.terrainHeight && { terrainHeight: item.terrainHeight }),
        ...(item.terrainSize && { terrainSize: item.terrainSize }),
        ...(item.speedMultiplier && { speedMultiplier: item.speedMultiplier }),
        saved: false,
        isDuplicate: true,
        ...(particleType === 'foliage'
          ? buildFoliageDuplicateProps(item, originalParticleObj)
          : {}),
      };

      // Drop undefined optional fields so handlers keep their defaults
      Object.keys(duplicateItem).forEach((key) => {
        if (duplicateItem[key] === undefined) delete duplicateItem[key];
      });

      // Create the scene object first, then register in Redux (sync can find it)
      particleHandler(scene, duplicateItem);
      store.dispatch(addParticle(duplicateItem));

      if (setPolyCount) setPolyCount((v) => v + 1);

      // Select the newly created object
      setTimeout(() => {
        const newParticleObj = scene.getObjectByName(newId);
        if (newParticleObj) {
          store.dispatch(selected_object(newParticleObj));
          selectObjectInScene(newParticleObj.name);
          if (transformControls) transformControls.attach(newParticleObj);
        }
      }, 50);

      store.dispatch(setLoadingMessage(false));
      toast.success('Particle duplicated successfully');
      return;
    }

    // Fallback: manual clone for unknown/unsupported particle types
    if (!originalParticleObj) {
      console.error('Original particle not found in scene');
      toast.error('Original particle not found');
      store.dispatch(setLoadingMessage(false));
      return;
    }

    const particleGroup = new Three.Group();
    particleGroup.name = newId;
    particleGroup.url = newId;
    particleGroup.deleteType = 'particle';
    particleGroup.objType =
      originalParticleObj.objType || item.type || item.objType;
    particleGroup.glb_type = originalParticleObj.glb_type || 'shapes';
    particleGroup.createdAt = new Date().toISOString();

    particleGroup.position.set(
      originalParticleObj.position.x,
      originalParticleObj.position.y,
      originalParticleObj.position.z
    );
    particleGroup.rotation.copy(originalParticleObj.rotation);
    particleGroup.scale.copy(originalParticleObj.scale);

    originalParticleObj.children.forEach((child) => {
      let clonedChild;
      try {
        clonedChild = child.clone(true);
      } catch (e) {
        return;
      }
      if (!clonedChild) return;

      if (clonedChild.material) {
        if (Array.isArray(clonedChild.material)) {
          clonedChild.material = clonedChild.material.map((mat) => {
            const clonedMat = mat.clone();
            if (clonedMat.color && clonedMat.color.clone) {
              clonedMat.color = clonedMat.color.clone();
            }
            return clonedMat;
          });
        } else {
          clonedChild.material = clonedChild.material.clone();
          if (clonedChild.material.color && clonedChild.material.color.clone) {
            clonedChild.material.color = clonedChild.material.color.clone();
          }
        }
      }

      clonedChild.traverse((nestedChild) => {
        if (nestedChild.material) {
          if (Array.isArray(nestedChild.material)) {
            nestedChild.material = nestedChild.material.map((mat) => {
              const clonedMat = mat.clone();
              if (clonedMat.color && clonedMat.color.clone) {
                clonedMat.color = clonedMat.color.clone();
              }
              return clonedMat;
            });
          } else {
            nestedChild.material = nestedChild.material.clone();
            if (
              nestedChild.material.color &&
              nestedChild.material.color.clone
            ) {
              nestedChild.material.color = nestedChild.material.color.clone();
            }
          }
        }
      });

      if (clonedChild.userData) {
        const clonedUserData = JSON.parse(JSON.stringify(clonedChild.userData));
        clonedUserData.animationId = null;
        clonedUserData.animateFunction = null;
        clonedChild.userData = clonedUserData;
      }

      particleGroup.add(clonedChild);
    });

    const deepClonedUserData = JSON.parse(JSON.stringify(originalUserData));

    particleGroup.userData = {
      ...deepClonedUserData,
      id: newId,
      type: particleType || item.type || deepClonedUserData.type,
      isDuplicate: true,
      originalId: item._id || item.id,
      saved: false,
      animationId: null,
      animateFunction: null,
      isPlaying: item.isPlaying ?? true,
      isStopped: item.isStopped ?? false,
      material_settings: deepClonedUserData.material_settings
        ? JSON.parse(JSON.stringify(deepClonedUserData.material_settings))
        : undefined,
    };

    scene.add(particleGroup);
    if (setPolyCount) setPolyCount((v) => v + 1);

    const duplicateItem = {
      id: newId,
      type: particleType || item.type || 'fire',
      parent: null,
      range: item.range || 1,
      intensity: item.intensity || 50,
      color: item.color || '#FFFFFF',
      position: {
        x: particleGroup.position.x,
        y: particleGroup.position.y,
        z: particleGroup.position.z,
      },
      rotation: {
        x: particleGroup.rotation.x,
        y: particleGroup.rotation.y,
        z: particleGroup.rotation.z,
      },
      scale: {
        x: particleGroup.scale.x,
        y: particleGroup.scale.y,
        z: particleGroup.scale.z,
      },
      isPlaying: item.isPlaying ?? true,
      isStopped: item.isStopped ?? false,
      particleCount: item.particleCount || 1000,
      size: item.size || 1.0,
      opacity: item.opacity || 0.9,
      sizeAttenuation: item.sizeAttenuation ?? true,
      blending: item.blending || 'AdditiveBlending',
      shape: item.shape || getDefaultParticleShape(item.type),
      shapeRotation: item.shapeRotation || 0,
      shapeScale: item.shapeScale || 1.0,
      velocity: item.velocity || { x: 0, y: 0, z: 0 },
      acceleration: item.acceleration || { x: 0, y: 0, z: 0 },
      turbulence: item.turbulence || 0.1,
      wind: item.wind || { x: 0, y: 0, z: 0 },
      life: item.life || 4.0,
      lifeVariation: item.lifeVariation || 0.2,
      colorVariation: item.colorVariation || 0.1,
      colorTransition: item.colorTransition ?? true,
      sizeVariation: item.sizeVariation || 0.3,
      sizeTransition: item.sizeTransition ?? true,
      ...(item.coreCount && { coreCount: item.coreCount }),
      ...(item.emberCount && { emberCount: item.emberCount }),
      ...(item.heatIntensity && { heatIntensity: item.heatIntensity }),
      ...(item.dropSize && { dropSize: item.dropSize }),
      ...(item.splashEffect && { splashEffect: item.splashEffect }),
      ...(item.flakeSize && { flakeSize: item.flakeSize }),
      ...(item.windEffect && { windEffect: item.windEffect }),
      ...(item.twinkleSpeed && { twinkleSpeed: item.twinkleSpeed }),
      ...(item.twinkleIntensity && { twinkleIntensity: item.twinkleIntensity }),
      ...(item.terrainWidth && { terrainWidth: item.terrainWidth }),
      ...(item.terrainHeight && { terrainHeight: item.terrainHeight }),
      ...(item.terrainSize && { terrainSize: item.terrainSize }),
      saved: false,
      isDuplicate: true,
    };

    store.dispatch(addParticle(duplicateItem));

    setTimeout(() => {
      const newParticleObj = scene.getObjectByName(newId);
      if (newParticleObj) {
        store.dispatch(selected_object(newParticleObj));
        if (transformControls) transformControls.attach(newParticleObj);
      } else {
        store.dispatch(selected_object(particleGroup));
        if (transformControls) transformControls.attach(particleGroup);
      }
    }, 100);

    selectObjectInScene(particleGroup.name);
    store.dispatch(setLoadingMessage(false));
    toast.success('Particle duplicated successfully');
  } catch (error) {
    console.error('Error duplicating particle:', error);
    toast.error('Failed to duplicate particle');
    store.dispatch(setLoadingMessage(false));
  }
}